import styled, { keyframes } from "styled-components";

// Animations
const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const typingAnimation = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-5px);
  }
`;

export const ChatContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 400px;
  height: ${(props) => (props.$isMinimized ? "60px" : "600px")};
  background: linear-gradient(135deg, #1a1a1a 0%, #2c2c2c 100%);
  border-radius: 15px;
  border: 2px solid #ff6b35;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  animation: ${slideUp} 0.3s ease-out;
  transition: height 0.3s ease;

  @media (max-width: 768px) {
    width: calc(100vw - 20px);
    height: ${(props) => (props.$isMinimized ? "60px" : "calc(100vh - 40%)")};
    bottom: 10px;
    right: 10px;
    left: 10px;
  }
`;

export const ChatHeader = styled.div`
  background: ${(props) =>
    props.$isMinimized
      ? "transparent"
      : "linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%)"};
  transition: all 0.3s ease-in-out;
  padding: 1rem 1.5rem;
  border-radius: 13px 13px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
  height: ${(props) => (props.$isMinimized ? "100%" : "unset")};
  cursor: pointer;
`;

export const ChatTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const ChatControls = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const ChatButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

export const ChatBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const MessagesContainer = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #333;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ff6b35;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #e55a2b;
  }
`;

export const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$isUser ? "flex-end" : "flex-start")};
  animation: ${fadeIn} 0.3s ease-out;
`;

export const MessageSender = styled.div`
  font-size: 0.8rem;
  color: #cccccc;
  margin-bottom: 0.3rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

export const MessageContent = styled.div`
  background: ${(props) =>
    props.$isUser
      ? "linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%)"
      : "#333"};
  color: white;
  padding: 0.8rem 1rem;
  border-radius: ${(props) =>
    props.$isUser ? "15px 15px 5px 15px" : "15px 15px 15px 5px"};
  max-width: 70%;
  word-wrap: break-word;
  line-height: 1.4;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

export const MessageTime = styled.div`
  font-size: 0.7rem;
  color: #888;
  margin-top: 0.3rem;
`;

export const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #cccccc;
  font-size: 0.9rem;
  background: #333;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  padding: 0.8rem 1rem;
  border-radius: 15px 15px 15px 5px;
  width: fit-content;

  .typing-dots {
    display: flex;
    gap: 2px;

    span {
      width: 5px;
      height: 5px;
      background: #ff6b35;
      border-radius: 50%;
      animation: ${typingAnimation} 1.4s infinite;

      &:nth-child(2) {
        animation-delay: 0.2s;
      }

      &:nth-child(3) {
        animation-delay: 0.4s;
      }
    }
  }
`;

export const ChatFooter = styled.div`
  padding: 1rem;
  border-top: 1px solid #333;
`;

export const InputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.5rem;
`;

export const ChatInput = styled.input`
  flex: 1;
  background: #333;
  border: 1px solid #555;
  border-radius: 20px;
  padding: 10px 20px;
  color: white;
  font-family: "Poppins-Regular";
  font-size: 0.9rem;

  height: 40px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }

  &::placeholder {
    color: #888;
  }
`;

export const SendButton = styled.button`
  background: ${(props) =>
    props.disabled
      ? "#555"
      : "linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%)"};
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.3s ease;
  font-size: 1rem;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #e55a2b 0%, #d44a1f 100%);
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
  }
`;

export const WelcomeMessage = styled.div`
  text-align: center;
  color: #888;
  font-size: 0.8rem;
  margin-top: 0.5rem;
`;

export const StatusIndicator = styled.span`
  font-size: 0.7rem;
  margin-left: 0.5rem;
  color: ${(props) => (props.$online ? "#27ae60" : "#f39c12")};
`;

export const ErrorMessage = styled.div`
  color: #f39c12;
  margin-top: 0.5rem;
  font-size: 0.7rem;
`;

// Confirm modal
export const ConfirmModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;

  .modal-content {
    background: #2a2a2a;
    padding: 2rem;
    border-radius: 8px;
    border: 1px solid #444;
    max-width: 500px;
    width: 90%;
    text-align: center;

    h3 {
      color: #fff;
      margin-bottom: 1rem;
      font-size: 1.3rem;
    }

    p {
      color: #ccc;
      margin-bottom: 2rem;
      line-height: 1.5;

      strong {
        color: #ff6b35;
      }
    }

    .modal-actions {
      display: flex;
      gap: 1rem;
      justify-content: center;
    }
  }
`;

// Action button (za akcije u tabeli)
export const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: ${({ size }) =>
    size === "small" ? "0.4rem 0.6rem" : "0.6rem 0.8rem"};
  border: none;
  border-radius: 4px;
  font-weight: 500;
  font-size: ${({ size }) => (size === "small" ? "0.75rem" : "0.85rem")};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: ${({ size }) => (size === "small" ? "auto" : "2.5rem")};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }

  &:not(:disabled):hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  ${({ variant }) => {
    switch (variant) {
      case "primary":
        return `
          background: #ff6b35;
          color: #fff;
          &:not(:disabled):hover {
            background: #e55a2b;
          }
        `;
      case "secondary":
        return `
          background: #666;
          color: #fff;
          &:not(:disabled):hover {
            background: #777;
          }
        `;
      case "danger":
        return `
          background: #e74c3c;
          color: #fff;
          &:not(:disabled):hover {
            background: #c0392b;
          }
        `;
      case "info":
        return `
          background: #3498db;
          color: #fff;
          &:not(:disabled):hover {
            background: #246a99;
          }
        `;
      case "success":
        return `
          background: #27ae60;
          color: #fff;
          &:not(:disabled):hover {
            background: #229954;
          }
        `;
      case "warning":
        return `
          background: #f39c12;
          color: #fff;
          &:not(:disabled):hover {
            background: #e67e22;
          }
        `;
      default:
        return `
          background: #555;
          color: #fff;
          &:not(:disabled):hover {
            background: #666;
          }
        `;
    }
  }}
`;

// CONFIRM MODAL STYLES
export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
`;

export const CloseBtn = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: #fff;
  border-radius: 50%;
  font-size: 1rem;
  width: 2rem;
  height: 2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.25);
  -webkit-backdrop-filter: blur(4px);
  background-color: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(4px);
  transition: background 0.3s;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #ff6b35;
  }
`;

export const ConfirmBox = styled.div`
  background: #1a1a1a;
  padding: 3rem 2rem;
  border-radius: 1.6rem;
  max-width: 400px;
  width: 100%;
  color: white;
  position: relative;
  text-align: center;

  h3 {
    font-family: "Poppins-SemiBold";
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #ff6b35;
  }

  p {
    font-family: "Poppins-Medium";
    font-size: 1rem;
    margin-bottom: 2rem;
    color: #ccc;
  }
`;

export const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

export const Cancel = styled.button`
  padding: 0.8rem 2rem;
  border-radius: 0.8rem;
  background: #333;
  color: white;
  border: none;
  font-family: "Poppins-SemiBold";
  font-size: 1rem;
  cursor: pointer;

  &:hover {
    background: #444;
  }
`;

export const Confirm = styled.button`
  padding: 0.8rem 2rem;
  border-radius: 0.8rem;
  background: #ff6b35;
  color: white;
  border: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.65rem;
  font-size: 1rem;
  font-family: "Poppins-SemiBold";
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};

  &:hover {
    background: #ff6b35;
  }
`;
